import React, { useState } from 'react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    useDisclosure,
    useToast,
    useColorModeValue
} from '@chakra-ui/react';
import { MdLocalShipping } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { AsyncPaginate } from 'react-select-async-paginate';
import axiosClient from '../axios-client';
import { useStateContext } from '../context/ContextProvider';

function DeliveryUpdateModal({ onSuccess }) {
    const { t } = useTranslation();
    const { user, currentBusiness } = useStateContext();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [selectedOrders, setSelectedOrders] = useState([]);
    const [loading, setLoading] = useState(false);

    // Styling colors for dark/light modes
    const selectTextColor = useColorModeValue('black', 'white');
    const selectBg = useColorModeValue('transparent', 'transparent');
    const selectMenuBg = useColorModeValue('white', '#2D3748');
    const selectBorderColor = useColorModeValue('#E2E8F0', 'rgba(255, 255, 255, 0.16)');
    const selectHoverBg = useColorModeValue('#EDF2F7', '#4A5568');

    const handleClose = () => {
        setSelectedOrders([]);
        onClose();
    };

    const loadOptions = async (search, loadedOptions, { page }) => {
        try {
            const res = await axiosClient.get(`/commandes/getPendingDeliveryOrders/?clientId=${user?.id}&search_term=${search}&page=${page}&business=${currentBusiness?.id}`);
            const results = res.data.results || res.data;
            const hasMore = res.data.next !== null && res.data.next !== undefined;

            return {
                options: results.map(order => ({
                    value: order.id,
                    label: `${order.code} - ${order.full_name || ''} - ${order.tel || ''}`
                })),
                hasMore: hasMore,
                additional: {
                    page: page + 1,
                },
            };
        } catch (error) {
            // // console.error('Error fetching orders', error);
            return { options: [], hasMore: false };
        }
    };

    const handleConfirm = async () => {
        if (selectedOrders.length === 0) return;
        setLoading(true);
        try {
            const orderIds = selectedOrders.map(o => o.value);

            await axiosClient.post('/commandes/bulk_actions/', {
                action: 'mark_delivered',
                user_id: user.id,
                order_ids: orderIds,
            });

            toast({
                description: t('Le statut de livraison a été mis à jour à "Livré".'),
                status: 'success',
                duration: 2500,
                isClosable: true,
                position: 'bottom-right',
            });
            handleClose();
            if (onSuccess) onSuccess();
        } catch (error) {
            toast({
                description: t('Erreur lors de la mise à jour.'),
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
        setLoading(false);
    };

    return (
        <>
            <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<MdLocalShipping />}
                onClick={onOpen}
            >
                {t('Marquer comme Livré')}
            </Button>

            <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent overflow="visible" bg={useColorModeValue('white', 'gray.800')}>
                    <ModalHeader color={selectTextColor}>{t('Mise à jour du statut de livraison')}</ModalHeader>
                    <ModalCloseButton color={selectTextColor} />

                    <ModalBody pb={4}>
                        <Text mb={4} fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                            {t('Cette action marquera les commandes sélectionnées comme entièrement traitées et livrées.')}
                        </Text>

                        <AsyncPaginate
                            isMulti
                            loadOptions={loadOptions}
                            onChange={setSelectedOrders}
                            value={selectedOrders}
                            placeholder={t('Sélectionner des commandes...')}
                            noOptionsMessage={() => t("Aucune commande trouvée")}
                            additional={{ page: 1 }}
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    backgroundColor: selectBg,
                                    borderColor: selectBorderColor,
                                    color: selectTextColor
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    zIndex: 9999,
                                    backgroundColor: selectMenuBg
                                }),
                                option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isFocused ? selectHoverBg : 'transparent',
                                    color: selectTextColor
                                }),
                                singleValue: (provided) => ({
                                    ...provided,
                                    color: selectTextColor
                                }),
                                multiValue: (provided) => ({
                                    ...provided,
                                    backgroundColor: selectHoverBg,
                                }),
                                multiValueLabel: (provided) => ({
                                    ...provided,
                                    color: selectTextColor
                                }),
                                multiValueRemove: (provided) => ({
                                    ...provided,
                                    color: selectTextColor,
                                    ':hover': {
                                        backgroundColor: 'red',
                                        color: 'white',
                                    },
                                }),
                                input: (provided) => ({
                                    ...provided,
                                    color: selectTextColor
                                })
                            }}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            colorScheme="blue"
                            isDisabled={selectedOrders.length === 0}
                            isLoading={loading}
                            onClick={handleConfirm}
                        >
                            {t('Changer le statut')}
                        </Button>
                        <Button variant="ghost" onClick={handleClose} ml={3} color={selectTextColor}>
                            {t('Annuler')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export default DeliveryUpdateModal;
